import React, { useEffect, useState, useCallback } from 'react';
import db from '../db/localdb';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/date';
import { createProductionRun, voidProductionRun } from '../sync/repo';
import { productionAPI } from '../services/api';
import BackButton from '../components/BackButton';

const EMPTY_INPUT = () => ({ product_id: '', qty_used: '' });
const EMPTY_FORM  = () => ({ product_id: '', qty_produced: '', notes: '', produced_at: '', inputs: [EMPTY_INPUT()] });

const fmtQ = (n) => parseFloat(Number(n).toFixed(3));

export default function Production() {
  const { business } = useAuth();
  const bid = business?.id;

  const [runs, setRuns]         = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM());
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const loadProducts = useCallback(async () => {
    if (!bid) return;
    const rows = await db.products
      .where('business_id').equals(bid)
      .filter(p => !p.voided_at)
      .toArray();
    rows.sort((a, b) => a.name.localeCompare(b.name));
    setProducts(rows);
  }, [bid]);

  const loadRuns = useCallback(async () => {
    if (!bid) return;
    try {
      const res = await productionAPI.list();
      setRuns(res.data.production_runs || []);
    } catch {
      // Offline: fall back to local IndexedDB (without inputs detail)
      const rows = await db.production_runs
        .where('business_id').equals(bid)
        .filter(r => !r.voided_at)
        .toArray();
      rows.sort((a, b) => new Date(b.produced_at) - new Date(a.produced_at));
      setRuns(rows);
    }
  }, [bid]);

  useEffect(() => {
    loadProducts();
    loadRuns();
  }, [loadProducts, loadRuns]);

  // ── form helpers ──────────────────────────────────────────────────────────

  const setInputField = (idx, field, value) => {
    setForm(f => {
      const inputs = f.inputs.map((inp, i) => i === idx ? { ...inp, [field]: value } : inp);
      return { ...f, inputs };
    });
  };

  const addInputRow = () => setForm(f => ({ ...f, inputs: [...f.inputs, EMPTY_INPUT()] }));

  const removeInputRow = (idx) => {
    setForm(f => ({ ...f, inputs: f.inputs.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.product_id) { setError('Select a finished product'); return; }
    const qty = parseFloat(form.qty_produced);
    if (!qty || qty <= 0) { setError('Enter a valid quantity produced'); return; }

    const validInputs = form.inputs
      .filter(i => i.product_id && parseFloat(i.qty_used) > 0)
      .map(i => ({ product_id: i.product_id, qty_used: parseFloat(i.qty_used) }));

    setSaving(true);
    try {
      await createProductionRun({
        business_id:  bid,
        product_id:   form.product_id,
        qty_produced: qty,
        notes:        form.notes || undefined,
        produced_at:  form.produced_at ? new Date(form.produced_at).toISOString() : undefined,
        inputs:       validInputs,
      });
      setForm(EMPTY_FORM());
      setShowForm(false);
      await Promise.all([loadRuns(), loadProducts()]);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to record production run');
    } finally {
      setSaving(false);
    }
  };

  const handleVoid = async (id) => {
    if (!window.confirm('Void this production run? Stock will be reversed.')) return;
    try {
      await voidProductionRun(id);
      await Promise.all([loadRuns(), loadProducts()]);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to void run');
    }
  };

  const productName = (pid) => products.find(p => p.id === pid)?.name || pid;

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '1.5rem 1rem 4rem' }}>
      <BackButton />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700 }}>Production</h1>
        <button
          onClick={() => { setShowForm(s => !s); setError(''); }}
          style={{
            background: 'var(--color-primary)', color: '#fff', border: 'none',
            borderRadius: 8, padding: '0.5rem 1.1rem', fontWeight: 600, cursor: 'pointer',
          }}
        >
          {showForm ? 'Cancel' : '+ New Run'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{
          background: 'var(--color-card)', borderRadius: 12, padding: '1.2rem',
          marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.85rem',
        }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Record Bottling / Assembly</h2>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Finished product</span>
            <select
              required
              value={form.product_id}
              onChange={e => setForm(f => ({ ...f, product_id: e.target.value }))}
              style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: '0.95rem' }}
            >
              <option value="">— select product —</option>
              {products.map(p => (
                <option key={p.id} value={p.id}>{p.name} (stock: {fmtQ(p.stock_qty)} {p.unit})</option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Qty produced</span>
            <input
              type="number" min="0.001" step="any" required
              value={form.qty_produced}
              onChange={e => setForm(f => ({ ...f, qty_produced: e.target.value }))}
              placeholder="e.g. 50"
              style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: '0.95rem' }}
            />
          </label>

          <div>
            <div style={{ fontSize: '0.82rem', color: 'var(--color-muted)', marginBottom: 6 }}>
              Raw materials consumed <span style={{ fontStyle: 'italic' }}>(optional)</span>
            </div>
            {form.inputs.map((inp, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <select
                  value={inp.product_id}
                  onChange={e => setInputField(idx, 'product_id', e.target.value)}
                  style={{ flex: 2, padding: '0.5rem', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: '0.9rem' }}
                >
                  <option value="">— input product —</option>
                  {products.filter(p => p.id !== form.product_id).map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <input
                  type="number" min="0.001" step="any"
                  value={inp.qty_used}
                  onChange={e => setInputField(idx, 'qty_used', e.target.value)}
                  placeholder="qty"
                  style={{ flex: 1, padding: '0.5rem', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: '0.9rem' }}
                />
                {form.inputs.length > 1 && (
                  <button type="button" onClick={() => removeInputRow(idx)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', fontSize: '1.1rem' }}>
                    ✕
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={addInputRow}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', fontSize: '0.88rem', padding: 0 }}>
              + Add material
            </button>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Date <span style={{ fontStyle: 'italic' }}>(optional)</span></span>
            <input
              type="datetime-local"
              value={form.produced_at}
              onChange={e => setForm(f => ({ ...f, produced_at: e.target.value }))}
              style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: '0.95rem' }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Notes <span style={{ fontStyle: 'italic' }}>(optional)</span></span>
            <input
              type="text"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. Batch #12, wildflower season"
              style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: '0.95rem' }}
            />
          </label>

          {error && <div style={{ color: 'var(--color-danger)', fontSize: '0.88rem' }}>{error}</div>}

          <button
            type="submit" disabled={saving}
            style={{
              background: 'var(--color-primary)', color: '#fff', border: 'none',
              borderRadius: 8, padding: '0.65rem', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Record Run'}
          </button>
        </form>
      )}

      {runs.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--color-muted)', marginTop: '3rem' }}>
          No production runs yet. Record your first bottling batch above.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {runs.map(run => (
            <div key={run.id} style={{
              background: 'var(--color-card)', borderRadius: 12, padding: '1rem',
              display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.97rem' }}>
                    {run.product_name || productName(run.product_id)}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--color-muted)' }}>
                    Produced: <strong>{fmtQ(run.qty_produced)}</strong> · {fmtDate(run.produced_at || run.created_at)}
                  </div>
                  {run.inputs && run.inputs.length > 0 && (
                    <div style={{ fontSize: '0.82rem', color: 'var(--color-muted)', marginTop: 3 }}>
                      Used: {run.inputs.map((inp, i) => (
                        <span key={i}>{i > 0 ? ', ' : ''}{fmtQ(inp.qty_used)} × {inp.product_name || productName(inp.product_id)}</span>
                      ))}
                    </div>
                  )}
                  {run.notes && (
                    <div style={{ fontSize: '0.82rem', color: 'var(--color-muted)', marginTop: 2, fontStyle: 'italic' }}>
                      {run.notes}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleVoid(run.id)}
                  style={{
                    background: 'none', border: '1px solid var(--color-border)',
                    borderRadius: 6, padding: '0.3rem 0.7rem', fontSize: '0.8rem',
                    cursor: 'pointer', color: 'var(--color-danger)', whiteSpace: 'nowrap',
                  }}
                >
                  Void
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
